import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Loader2, SlidersHorizontal } from "lucide-react";

export default function AdminSettings() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/settings"); setRows(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load settings."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key, value) => {
    setBusy(key);
    try {
      await api.put("/admin/settings", { key, value });
      setRows((rs) => rs.map((r) => (r.key === key ? { ...r, value } : r)));
      toast.success("Setting updated.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to update."); }
    finally { setBusy(""); }
  };

  if (!rows) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-settings">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><SlidersHorizontal className="h-7 w-7 text-[#24468C]" strokeWidth={1.75} /> Settings</h1>
        <p className="text-muted-foreground text-sm">Platform-wide feature toggles. Changes apply instantly.</p>
      </div>

      <div className="space-y-3">
        {rows.map((r) => (
          <Card key={r.key} className="border-zinc-200"><CardContent className="p-5 flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold">{r.label}</p>
              <p className="text-sm text-muted-foreground">{r.desc}</p>
            </div>
            {r.type === "number" ? (
              <NumberSetting r={r} busy={busy} onSave={(v) => toggle(r.key, v)} />
            ) : (
              <Switch checked={r.value} disabled={busy === r.key} onCheckedChange={(v) => toggle(r.key, v)} data-testid={`setting-toggle-${r.key}`} />
            )}
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}

function NumberSetting({ r, busy, onSave }) {
  const [val, setVal] = useState(r.value);
  useEffect(() => { setVal(r.value); }, [r.value]);
  const commit = () => {
    const n = Number(val);
    if (!isNaN(n) && n !== r.value) onSave(n);
    else setVal(r.value);
  };
  return (
    <Input
      type="number" min={r.min} max={r.max} value={val} disabled={busy === r.key}
      onChange={(e) => setVal(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      className="w-24 text-right" data-testid={`setting-number-${r.key}`}
    />
  );
}
